import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SingAlongService, SingAlongPurchase, SingAlongArchive } from '../services/SingAlongService';
import { useSingAlongBasePath } from '../hooks/useSingAlongBasePath';
import { TvFocusableInput } from '../components/TvFocusableInput';
import { Loader as Loader2, Copy, Check, Users, RotateCcw, Clock, Trophy, Trash2, Archive, TriangleAlert as AlertTriangle, X, Mail, Send, Download } from 'lucide-react';

export const SingAlongDashboard: React.FC = () => {
  const navigate = useNavigate();
  const basePath = useSingAlongBasePath();
  const [purchase, setPurchase] = useState<SingAlongPurchase | null>(null);
  const [league, setLeague] = useState<any>(null);
  const [participants, setParticipants] = useState<any[]>([]);
  const [archives, setArchives] = useState<SingAlongArchive[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'invite' | 'participants' | 'settings' | 'archives'>('invite');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [editPasscode, setEditPasscode] = useState('');
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteSubject, setInviteSubject] = useState('');
  const [inviteBody, setInviteBody] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);

  const loadData = useCallback(async () => {
    const user = await SingAlongService.getCurrentUser();
    if (!user) { navigate(`/${basePath}/signup`, { replace: true }); return; }

    try {
      const activePurchase = await SingAlongService.getActivePurchase();
      if (!activePurchase) { navigate(`/${basePath}/plans`, { replace: true }); return; }
      if (!activePurchase.league_id) { navigate(`/${basePath}/setup`, { replace: true }); return; }

      setPurchase(activePurchase);

      const [leagueData, participantData, archiveData] = await Promise.all([
        SingAlongService.getLeagueDetails(activePurchase.league_id),
        SingAlongService.getParticipants(activePurchase.league_id),
        SingAlongService.getArchives(activePurchase.id),
      ]);

      setLeague(leagueData);
      setParticipants(participantData);
      setArchives(archiveData);
      setEditPasscode(leagueData.passcode || '');
      setEditName(leagueData.name || '');
      setInviteSubject(`You're invited to sing along with us!`);
      setInviteBody(`Hey there!\n\nI'm hosting a sing-along called "${leagueData.name}" and I'd love for you to join.\n\nHere's how to join:\n1. Go to the link below\n2. Enter the passcode: ${leagueData.passcode}\n3. Pick a song and sing your heart out!\n\nLink: ${window.location.origin}/${basePath}/${leagueData.slug}\n\nSee you on the leaderboard!`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [navigate, basePath]);

  useEffect(() => { loadData(); }, [loadData]);

  const singAlongUrl = league ? `${window.location.origin}/${basePath}/${league.slug}` : '';

  const copyLink = async () => {
    if (!league) return;
    await navigator.clipboard.writeText(singAlongUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const timeRemaining = () => {
    if (!purchase) return '';
    const diff = new Date(purchase.expires_at).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h remaining`;
    return `${hours}h remaining`;
  };

  const isExpired = purchase ? new Date(purchase.expires_at) < new Date() : false;

  const handleSaveSettings = async () => {
    if (!league) return;
    setSaving(true);
    try {
      if (editName !== league.name) await SingAlongService.updateLeagueName(league.id, editName);
      if (editPasscode !== league.passcode) await SingAlongService.updatePasscode(league.id, editPasscode);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!purchase || !league) return;
    setResetting(true);
    try {
      await SingAlongService.resetKiosk(purchase.id, league.id);
      setResetModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    try {
      await SingAlongService.removeParticipant(participantId);
      setParticipants(prev => prev.filter(p => p.id !== participantId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    }
  };

  const handleSendInvites = async () => {
    const emails = inviteEmails
      .split(/[,\n]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    if (emails.length === 0) return;

    setSendingInvites(true);
    try {
      await SingAlongService.sendInvites({
        emails,
        subject: inviteSubject,
        body: inviteBody,
        leagueName: league?.name || 'Sing-Along',
        leagueSlug: league?.slug,
      });
      setInviteSent(true);
      setInviteEmails('');
      setTimeout(() => setInviteSent(false), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invites');
    } finally {
      setSendingInvites(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-600/10 via-transparent to-blue-600/10 pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black">{league?.name || 'Your Sing-Along'}</h1>
            <div className="flex items-center gap-3 mt-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                isExpired ? 'bg-red-500/15 text-red-400' : 'bg-green-500/15 text-green-400'
              }`}>
                <Clock size={12} />
                {timeRemaining()}
              </span>
              <span className="text-xs text-white/40">{participants.length} participants</span>
            </div>
          </div>
          <Link
            to={`/${basePath}/${league?.slug}/leaderboard`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/15 rounded-xl text-sm font-medium hover:bg-white/10 transition"
          >
            View Leaderboard
            <Trophy size={14} />
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white/[0.03] border border-white/10 rounded-xl p-1">
          {(['invite', 'participants', 'settings', 'archives'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold transition capitalize ${
                tab === t ? 'bg-orange-500/20 text-orange-400' : 'text-white/50 hover:text-white/70'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Invite Tab */}
        {tab === 'invite' && (
          <div className="space-y-6">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4">
              <h2 className="font-bold text-lg">Share With Friends</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-white/40 uppercase tracking-wider font-medium">Sing-Along URL</label>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 px-4 py-3 bg-black/30 border border-white/10 rounded-xl text-sm text-white/80 truncate">
                      {singAlongUrl}
                    </code>
                    <button
                      onClick={copyLink}
                      className="px-4 py-3 bg-orange-500/10 border border-orange-500/30 rounded-xl text-orange-400 hover:bg-orange-500/20 transition"
                    >
                      {copied ? <Check size={18} /> : <Copy size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/40 uppercase tracking-wider font-medium">Passcode</label>
                  <div className="mt-1 px-4 py-3 bg-black/30 border border-white/10 rounded-xl">
                    <span className="text-2xl font-mono font-bold tracking-[0.3em] text-orange-400">
                      {league?.passcode || '----'}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-white/40">
                Share the URL and passcode with your friends. They'll enter their name and the passcode to join.
              </p>
            </div>

            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-orange-400" />
                <h2 className="font-bold text-lg">Send Email Invitations</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider font-medium mb-1.5">
                    Email Addresses (comma or newline separated)
                  </label>
                  <TvFocusableInput
                    multiline
                    rows={3}
                    value={inviteEmails}
                    onChange={(e) => setInviteEmails(e.target.value)}
                    enterKeyHint="enter"
                    placeholder="friend@example.com, another@example.com"
                    className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-orange-500/50 transition resize-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider font-medium mb-1.5">
                    Subject
                  </label>
                  <TvFocusableInput
                    value={inviteSubject}
                    onChange={(e) => setInviteSubject(e.target.value)}
                    enterKeyHint="next"
                    className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white focus:outline-none focus:border-orange-500/50 transition text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/40 uppercase tracking-wider font-medium mb-1.5">
                    Message
                  </label>
                  <TvFocusableInput
                    multiline
                    rows={8}
                    value={inviteBody}
                    onChange={(e) => setInviteBody(e.target.value)}
                    enterKeyHint="enter"
                    className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white focus:outline-none focus:border-orange-500/50 transition resize-none text-sm leading-relaxed"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSendInvites}
                  disabled={sendingInvites || !inviteEmails.trim()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition disabled:opacity-50"
                >
                  {sendingInvites ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  Send Invitations
                </button>
                {inviteSent && (
                  <span className="text-green-400 text-sm font-medium flex items-center gap-1.5">
                    <Check size={16} /> Invitations sent!
                  </span>
                )}
              </div>
            </div>

            {isExpired && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 text-center">
                <Clock size={24} className="text-amber-400 mx-auto mb-2" />
                <p className="text-amber-300 font-semibold">Your pass has expired</p>
                <p className="text-sm text-amber-300/70 mt-1">
                  Existing results are still viewable, but no new recordings can be made.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Participants Tab */}
        {tab === 'participants' && (
          <div className="space-y-4">
            {/* Export Participants */}
            {participants.some((p: any) => p.kiosk_user?.email && !p.kiosk_user.email.endsWith('.local')) && (
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    const rows = participants
                      .filter((p: any) => p.kiosk_user?.email && !p.kiosk_user.email.endsWith('.local'))
                      .map((p: any) => `${p.display_name},${p.kiosk_user.email},${p.kiosk_user?.marketing_opt_in ? 'Yes' : 'No'}`);
                    const csv = ['Name,Email,Marketing Opted In', ...rows].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${league?.name || 'contest'}-participants.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded-xl text-sm font-semibold transition"
                >
                  <Download size={16} />
                  Export Participants ({participants.filter((p: any) => p.kiosk_user?.email && !p.kiosk_user.email.endsWith('.local')).length})
                </button>
              </div>
            )}

            <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
              {participants.length === 0 ? (
                <div className="p-12 text-center text-white/40">
                  <Users size={32} className="mx-auto mb-3 opacity-50" />
                  <p>No participants yet. Share your link to get started.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03]">
                    <tr className="text-left text-white/50 text-xs uppercase tracking-wide">
                      <th className="px-5 py-3 font-medium">Name</th>
                      <th className="px-5 py-3 font-medium">Email</th>
                      <th className="px-5 py-3 font-medium">Marketing</th>
                      <th className="px-5 py-3 font-medium">Joined</th>
                      <th className="px-5 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {participants.map((p: any) => (
                      <tr key={p.id} className="hover:bg-white/[0.02] transition">
                        <td className="px-5 py-4 font-medium text-white">{p.display_name}</td>
                        <td className="px-5 py-4 text-white/60 text-xs">
                          {p.kiosk_user?.email && !p.kiosk_user.email.endsWith('.local')
                            ? p.kiosk_user.email
                            : <span className="text-white/25">-</span>}
                        </td>
                        <td className="px-5 py-4">
                          {p.kiosk_user?.marketing_opt_in ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-medium border border-emerald-500/20">
                              <Check size={12} /> Opted in
                            </span>
                          ) : (
                            <span className="text-white/25 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-white/40 text-xs">
                          {new Date(p.joined_at).toLocaleDateString()}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            onClick={() => handleRemoveParticipant(p.id)}
                            className="p-2 text-white/30 hover:text-red-400 transition"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {tab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4">
              <h2 className="font-bold text-lg">Kiosk Settings</h2>
              <div>
                <label className="block text-sm text-white/50 mb-1.5">Kiosk Name</label>
                <TvFocusableInput
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  enterKeyHint="next"
                  className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white focus:outline-none focus:border-orange-500/50 transition"
                />
              </div>
              <div>
                <label className="block text-sm text-white/50 mb-1.5">Passcode</label>
                <TvFocusableInput
                  value={editPasscode}
                  onChange={(e) => setEditPasscode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8))}
                  enterKeyHint="done"
                  className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white font-mono uppercase tracking-widest focus:outline-none focus:border-orange-500/50 transition"
                />
              </div>
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="px-6 py-2.5 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

            {!isExpired && (
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                <h2 className="font-bold text-lg mb-2">Reset Kiosk</h2>
                <p className="text-sm text-white/50 mb-4">
                  Save current results and start fresh. All participants and scores will be archived.
                </p>
                <button
                  onClick={() => setResetModalOpen(true)}
                  className="px-6 py-2.5 bg-red-500/10 border border-red-500/30 text-red-400 font-semibold rounded-xl hover:bg-red-500/20 transition"
                >
                  <RotateCcw size={16} className="inline mr-2" />
                  Reset Kiosk
                </button>
              </div>
            )}
          </div>
        )}

        {/* Archives Tab */}
        {tab === 'archives' && (
          <div className="space-y-4">
            {archives.length === 0 ? (
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-12 text-center text-white/40">
                <Archive size={32} className="mx-auto mb-3 opacity-50" />
                <p>No archived results yet. Archives are created when you reset your kiosk.</p>
              </div>
            ) : (
              archives.map((archive) => (
                <div key={archive.id} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-white/50">
                      {new Date(archive.archived_at).toLocaleString()}
                    </span>
                    <span className="text-xs text-white/30">
                      {archive.archive_data.submission_count} submissions
                    </span>
                  </div>
                  {archive.archive_data.participants && archive.archive_data.participants.length > 0 && (
                    <div className="space-y-1">
                      {archive.archive_data.participants.slice(0, 5).map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-white/70">{p.display_name}</span>
                          <span className="text-orange-400 font-medium">{p.score?.toFixed(1) || '--'}</span>
                        </div>
                      ))}
                      {archive.archive_data.participants.length > 5 && (
                        <p className="text-xs text-white/30 pt-1">
                          +{archive.archive_data.participants.length - 5} more
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      {resetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <AlertTriangle size={20} className="text-amber-400" />
                Reset Kiosk
              </h3>
              <button onClick={() => setResetModalOpen(false)} className="text-white/40 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <p className="text-white/60 text-sm mb-4">
              This will save current results to your archives, then clear all participants and submissions so you can start a new round.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setResetModalOpen(false)}
                className="flex-1 py-2.5 bg-white/5 border border-white/15 rounded-xl font-semibold text-white/70 hover:bg-white/10 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={resetting}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {resetting ? <Loader2 size={16} className="animate-spin" /> : 'Save & Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingAlongDashboard;
